import { call, put, SagaGenerator, select, take, takeEvery, takeLatest } from "typed-redux-saga";
import { produce } from "immer";
import { AnyAction } from "redux";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { FormActivity } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentServiceFactory, EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { addDataHolder, createCustomNotificationAction } from "../../../app/actions";
import {
	ButtonAction,
	createEventButtonActionMatcher,
	createRowButtonActionMatcher,
	RowButtonAction,
	valueChangeActionMatcher
} from "../../../utilities/actionUtils";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { assertTrue } from "../../../utilities/assert";
import { ContactSelectionModalDescriptor } from "../../contactData/descriptors";
import { selectAvailableClients } from "../../../utilities/selectors/modulfDataSelectors";
import { isNonNullable } from "../../../utilities/typeGuardUtils";
import { openTaskDocument } from "../../processOverview/store/actions";
import { ContactData, isContactData } from "../../contactData/types";

import { InvitationModalDescriptor } from "../descriptors";
import { createInvitations, deleteInvitation, getInvitations, resendInvitation, revokeInvitation } from "../api";
import { confirmResponse, loadInvitationsAction } from "../actions";
import {
	ExistingInvitationEntry,
	InvitationConfirmModalData,
	InvitationDocumentData,
	InvitationDocumentRoot,
	invitationDtoToEntry,
	isExistingInvitationEntry,
	isInvitationDocumentData,
	isInvitationDocumentRoot,
	RecipientEntry
} from "../types";
import { INVITE_MANUAL_APP_KEY } from "../components/InvitationModal";

import SingleDocumentData = FormActivity.Data.SingleDocumentData;

/** A12 document path pointing to the repeatable `recipients` group inside `newInvitations`. */
const INVITATION_RECIPIENTS_PATH = "root[1]/newInvitations[1]/recipients[0]";

interface InvitationContext {
	appKey: string;
	data: SingleDocumentData;
	root: InvitationDocumentRoot;
	descriptor: InvitationModalDescriptor;
}

type PathEntry = { elementName?: unknown; index?: unknown };

export function* invitationActionsSaga(): SagaGenerator<void> {
	yield* takeLatest(createEventButtonActionMatcher("send_invitations"), handleSendInvitations);
	yield* takeEvery(createRowButtonActionMatcher("revoke_invitation"), handleRevoke);
	yield* takeEvery(createRowButtonActionMatcher("resend_invitation"), handleResend);
	yield* takeEvery(createRowButtonActionMatcher("delete_invitation"), handleDelete);
	yield* takeEvery(createRowButtonActionMatcher("open_process"), handleOpenProcess);
	yield* takeEvery(loadInvitationsAction.match, handleLoadInvitations);
	yield* takeEvery(createEventButtonActionMatcher("add_recipients_from_contacts"), handleAddRecipientsFromContacts);
	yield* takeEvery(createEventButtonActionMatcher("show_invitation_contacts"), handleAddRecipientsFromContacts);
	yield* takeEvery(valueChangeActionMatcher(), handleAddRecipientToContactData);
}

const documentService = new DocumentServiceFactory().getDocumentService();

function extractTokenFromRowPath(
	rowPath: EntityInstancePath,
	data: SingleDocumentData
): { token: string; email: string } | undefined {
	const existingInvitation = documentService.getAssignedObject(data.document, rowPath);
	if (!isExistingInvitationEntry(existingInvitation)) {
		return;
	}
	return { token: existingInvitation.token, email: existingInvitation.email };
}

function* setExistingInvitations(
	activityId: string,
	ctx: InvitationContext,
	existingInvitations: ExistingInvitationEntry[]
): SagaGenerator<void> {
	yield* put(
		ActivityActions.setData({
			activityId,
			data: {
				document: {
					...ctx.data.document,
					root: { ...ctx.root, existingInvitations }
				}
			}
		})
	);
}

function* handleSendInvitations(action: ButtonAction): SagaGenerator<void> {
	const { activityId } = action.payload;
	const ctx = yield* call(getInvitationContext, activityId);
	if (!ctx) {
		return;
	}

	const { newInvitations } = ctx.root;
	const recipients = (newInvitations.recipients ?? [])
		.filter((item): item is RecipientEntry => !!item.email)
		.map(item => ({ email: item.email }));

	if (recipients.length === 0) {
		return;
	}

	yield* put(ApplicationActions.setBusy(true));
	try {
		const response = yield* call(createInvitations, ctx.appKey, {
			expiresIn: newInvitations.expiresIn,
			recipients: recipients.filter((it): it is { email: string } => it.email !== undefined),
			client: newInvitations.client
		});

		const newEntries = response.created.map(invitationDtoToEntry);
		const skippedEmails = new Set(response.skipped.map(s => s.email));
		const skippedRecipients = (newInvitations.recipients ?? []).filter(
			r => isNonNullable(r.email) && skippedEmails.has(r.email)
		);

		// Re-read activity data after the async call to avoid stale state
		const updatedCtx = yield* call(getInvitationContext, activityId);
		if (updatedCtx) {
			yield* put(
				ActivityActions.setData({
					activityId,
					data: {
						document: {
							...updatedCtx.data.document,
							root: {
								...updatedCtx.root,
								existingInvitations: [...newEntries, ...updatedCtx.root.existingInvitations],
								newInvitations: {
									...updatedCtx.root.newInvitations,
									recipients: skippedRecipients
								}
							}
						}
					} satisfies InvitationDocumentData
				})
			);
		}

		if (response.skipped.length > 0) {
			const skippedEmailList = response.skipped.map(s => s.email).join(", ");
			yield* put(
				createCustomNotificationAction({
					severity: "warning",
					message: {
						key: "invitation.skipped",
						defaults: { de: `Folgende Einladungen konnten nicht versendet werden: ${skippedEmailList}` }
					},
					icon: "warning",
					activityId
				})
			);
		} else {
			yield* put(
				createCustomNotificationAction({
					severity: "success",
					message: {
						key: "invitation.sent",
						defaults: { de: "Einladungen wurden erfolgreich versendet." }
					},
					icon: "done",
					activityId
				})
			);
		}
	} catch (error) {
		yield* call(putErrorNotification, "Einladungen konnten nicht versendet werden.", error, activityId);
	} finally {
		yield* put(ApplicationActions.setBusy(false));
	}
}

function* handleDelete(action: RowButtonAction): SagaGenerator<void> {
	const { activityId } = action.payload;
	const rowPath = action.payload.engineEvent.payload.rowPath;
	const ctx = yield* call(getInvitationContext, activityId);
	if (!ctx) {
		return;
	}

	const entry = extractTokenFromRowPath(rowPath, ctx.data);
	if (!entry) {
		return;
	}

	const confirmed = yield* call(
		requestAndWaitForConfirmation,
		"Einladung löschen",
		`Möchten Sie diese Einladung für ${entry.email} wirklich löschen?`,
		"Löschen"
	);
	if (!confirmed) {
		return;
	}

	yield* put(ApplicationActions.setBusy(true));
	try {
		yield* call(deleteInvitation, ctx.appKey, entry.token);
		yield* call(
			setExistingInvitations,
			activityId,
			ctx,
			ctx.root.existingInvitations.filter(e => e.token !== entry.token)
		);
		yield* put(
			createCustomNotificationAction({
				severity: "success",
				message: {
					key: "invitation.deleted",
					defaults: { de: `Einladung für ${entry.email} wurde erfolgreich gelöscht.` }
				},
				icon: "done",
				activityId
			})
		);
	} catch (error) {
		yield* call(
			putErrorNotification,
			`Einladung für ${entry.email} konnte nicht gelöscht werden.`,
			error,
			activityId
		);
	} finally {
		yield* put(ApplicationActions.setBusy(false));
	}
}

function* handleRevoke(action: RowButtonAction): SagaGenerator<void> {
	const { activityId } = action.payload;
	const rowPath = action.payload.engineEvent.payload.rowPath;
	const ctx = yield* call(getInvitationContext, activityId);
	if (!ctx) {
		return;
	}

	const entry = extractTokenFromRowPath(rowPath, ctx.data);
	if (!entry) {
		return;
	}
	const confirmed = yield* call(
		requestAndWaitForConfirmation,
		"Einladung widerrufen",
		`Möchten Sie diese Einladung für ${entry.email} wirklich widerrufen?`,
		"Widerrufen"
	);
	if (!confirmed) {
		return;
	}

	yield* put(ApplicationActions.setBusy(true));
	try {
		const updatedEntry = invitationDtoToEntry(yield* call(revokeInvitation, ctx.appKey, entry.token));
		yield* call(
			setExistingInvitations,
			activityId,
			ctx,
			ctx.root.existingInvitations.map(e => (e.token === entry.token ? updatedEntry : e))
		);
		yield* put(
			createCustomNotificationAction({
				severity: "success",
				message: {
					key: "invitation.revoked",
					defaults: { de: `Einladung für ${entry.email} wurde erfolgreich widerrufen.` }
				},
				icon: "done",
				activityId
			})
		);
	} catch (error) {
		yield* call(
			putErrorNotification,
			`Einladung für ${entry.email} konnte nicht widerrufen werden.`,
			error,
			activityId
		);
	} finally {
		yield* put(ApplicationActions.setBusy(false));
	}
}

function* handleResend(action: RowButtonAction): SagaGenerator<void> {
	const { activityId } = action.payload;
	const rowPath = action.payload.engineEvent.payload.rowPath;

	const ctx = yield* call(getInvitationContext, activityId);
	if (!ctx) {
		return;
	}

	const entry = extractTokenFromRowPath(rowPath, ctx.data);
	if (!entry) {
		return;
	}
	const confirmed = yield* call(
		requestAndWaitForConfirmation,
		"Einladung neu versenden",
		`Möchten Sie diese Einladung für ${entry.email} wirklich neu versenden?`,
		"Neu versenden"
	);
	if (!confirmed) {
		return;
	}

	yield* put(ApplicationActions.setBusy(true));
	try {
		const newEntry = invitationDtoToEntry(yield* call(resendInvitation, ctx.appKey, entry.token));
		yield* call(setExistingInvitations, activityId, ctx, [
			newEntry,
			...ctx.root.existingInvitations.filter(e => e.token !== entry.token)
		]);
		yield* put(
			createCustomNotificationAction({
				severity: "success",
				message: {
					key: "invitation.resent",
					defaults: { de: `Einladung für ${entry.email} wurde erfolgreich neu versendet.` }
				},
				icon: "done",
				activityId
			})
		);
	} catch (error) {
		yield* call(
			putErrorNotification,
			`Einladung für ${entry.email} konnte nicht neu versendet werden.`,
			error,
			activityId
		);
	} finally {
		yield* put(ApplicationActions.setBusy(false));
	}
}

function* handleLoadInvitations(action: ReturnType<typeof loadInvitationsAction>): SagaGenerator<void> {
	const { activityId, applicationKey } = action.payload;

	yield* put(ApplicationActions.setBusy(true));
	try {
		const existingInvitations = (yield* call(getInvitations, applicationKey)).map(invitationDtoToEntry);
		const ctx = yield* call(getInvitationContext, activityId);
		if (ctx) {
			yield* call(setExistingInvitations, activityId, ctx, existingInvitations);
		}
	} catch (error) {
		console.error("Error reloading invitations", error);
	} finally {
		yield* put(ApplicationActions.setBusy(false));
	}
}

function* handleOpenProcess(action: RowButtonAction): SagaGenerator<void> {
	const {
		activityId,
		engineEvent: {
			payload: { rowPath }
		}
	} = action.payload;
	const context = yield* call(getInvitationContext, activityId);
	if (!context) {
		return;
	}

	const existingInvitation = documentService.getAssignedObject(context.data.document, rowPath);
	const docRef = isExistingInvitationEntry(existingInvitation) && existingInvitation.relatedDocRef;
	if (!docRef) {
		return undefined;
	}
	yield* put(ApplicationActions.setBusy(true));
	yield* put(ActivityActions.setDirty({ activityId, dirty: false }));
	yield* put(ActivityActions.cancel({ activityId }));
	yield* put(openTaskDocument({ instance: docRef, initiatingActivityId: context.descriptor.initiatingActivityId }));
}

function* getInvitationContext(activityId: string): SagaGenerator<InvitationContext | undefined> {
	const activity = yield* select(ActivitySelectors.activityById(activityId));
	assertTrue(InvitationModalDescriptor.isInstance(activity?.descriptor));
	const data = yield* select(ActivitySelectors.data(activityId));
	if (!FormActivity.Data.SingleDocumentData.isInstance(data)) {
		return;
	}
	const root = data.document.root;
	assertTrue(isInvitationDocumentRoot(root));
	return { appKey: activity.descriptor.applicationKey, data, root, descriptor: activity.descriptor };
}

function* handleAddRecipientsFromContacts(action: ButtonAction): SagaGenerator<void> {
	const { activityId } = action.payload;
	const activity = yield* select(ActivitySelectors.activityById(activityId));
	if (!InvitationModalDescriptor.isInstance(activity?.descriptor)) {
		return;
	}

	const data = yield* select(ActivitySelectors.data(activityId));
	const invitationRoot = isInvitationDocumentData(data) ? data.document.root : undefined;
	const preselectedClient = invitationRoot?.newInvitations.client;
	const availableClients = yield* select(selectAvailableClients(activity.descriptor.applicationKey));
	const clients = isNonNullable(preselectedClient) ? preselectedClient : availableClients.join(",");

	yield* put(ApplicationActions.setBusy(true));
	yield* put(
		ActivityActions.create({
			initiatingActivityId: activityId,
			activityDescriptor: ContactSelectionModalDescriptor.build({
				sourceActivityId: activityId,
				sourceAppKeys: activity.descriptor.sourceAppKeys,
				targetGroupPath: INVITATION_RECIPIENTS_PATH,
				entryMode: "email",
				clients
			})
		})
	);
}

function* requestAndWaitForConfirmation(title: string, message: string, buttonLabel: string): SagaGenerator<boolean> {
	yield* put(
		ActivityActions.create({
			activityDescriptor: {
				modal: "invitation_confirm_modal",
				stack_view: "true"
			},
			data: {
				title,
				message,
				buttonLabel
			} satisfies InvitationConfirmModalData,
			loadingState: "without"
		})
	);
	const responseAction = yield* take(confirmResponse);
	return responseAction.payload.confirmed;
}

function hasInvitationRecipientEmailPath(path: unknown): boolean {
	if (!Array.isArray(path) || path.length != 4) {
		return false;
	}

	const expectedElementNames = ["root", "newInvitations", "recipients", "email"];
	return expectedElementNames.every((elementName, index) => {
		const entry = path[index] as PathEntry;
		return entry?.elementName === elementName;
	});
}

export function* handleAddRecipientToContactData(action: AnyAction): SagaGenerator<void> {
	const { activityId, engineEvent } = action.payload;
	if (!hasInvitationRecipientEmailPath(engineEvent.payload?.path)) {
		return;
	}

	const value = engineEvent.payload?.value as string | undefined;
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	if (!value || value.trim() === "" || !emailRegex.test(value)) {
		return;
	}

	const dataHolder = yield* select(ActivitySelectors.dataHolderByDescriptor(activityId, { type: "contactData" }));
	if (!dataHolder) {
		const contactDataholder: Activity.DataHolder = {
			descriptor: { type: "contactData" },
			dirty: false,
			savingState: "saved",
			loadingState: "missing",
			slices: {}
		};
		yield* put(addDataHolder({ dataHolder: contactDataholder, activityId }));
	}

	const currentData = isContactData(dataHolder?.data) ? dataHolder?.data : ({ values: {}, names: {} } as ContactData);

	const updatedData = produce(currentData, draft => {
		const valueExistsInAnyGroup = Object.values(draft.values).some(
			entries => Array.isArray(entries) && entries.some(entry => entry.value === value)
		);

		if (!(INVITE_MANUAL_APP_KEY in draft.names)) {
			draft.names[INVITE_MANUAL_APP_KEY] = INVITE_MANUAL_APP_KEY;
		}
		if (!Array.isArray(draft.values[INVITE_MANUAL_APP_KEY])) {
			draft.values[INVITE_MANUAL_APP_KEY] = [];
		}
		if (!valueExistsInAnyGroup) {
			draft.values[INVITE_MANUAL_APP_KEY].push({ value, label: value });
		}
	});

	yield* put(
		ActivityActions.setDataByDescriptor({
			activityId,
			descriptor: { type: "contactData" },
			data: updatedData
		})
	);
}
